import { useState, useMemo } from 'react';
import { Minus, Plus } from 'lucide-react';
import { fmt, getPalier } from './moana-data';
import { Eyebrow } from './MoanaHero';

export const MoanaSimulateur = () => {
  const [simVal, setSimVal] = useState(5);

  const simData = useMemo(() => {
    const { rate, label } = getPalier(simVal);
    const bul = rate * simVal;
    return {
      label,
      bul,
      essentiel: { m: bul, a: bul * 12 },
      confort: { m: bul + 3500, a: (bul + 3500) * 12 },
      serenite: { m: bul + 6500, a: (bul + 6500) * 12 },
    };
  }, [simVal]);

  return (
    <section id="simulateur" className="py-20 bg-[#F8FAFC]" data-testid="simulateur-section">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="mb-9">
          <Eyebrow>Estimez votre budget</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1F2937]">
            Simulateur de cout <em className="italic text-[#F59E0B]">mensuel</em>
          </h2>
          <p className="text-sm text-gray-500 leading-relaxed mt-2 max-w-[560px]">
            Saisissez votre effectif et comparez les trois packs en temps réel.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="bg-[#0F766E] p-8">
            <h3 className="text-xl font-bold text-white mb-1.5">Calculez votre budget Moana Paye</h3>
            <p className="text-sm text-white/55">Résultat immédiat selon votre effectif et le pack choisi.</p>
          </div>
          <div className="p-7 border-b border-gray-200 flex items-center gap-6 flex-wrap">
            <span className="text-sm font-semibold text-[#1F2937] whitespace-nowrap">Nombre de salariés :</span>
            <div className="flex items-center">
              <button
                onClick={() => setSimVal(Math.max(1, simVal - 1))}
                className="w-9 h-9 border border-gray-200 bg-[#F8FAFC] rounded-l-md flex items-center justify-center text-[#0D9488] hover:bg-[#0D9488] hover:text-white hover:border-[#0D9488] transition-all"
                data-testid="sim-minus"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                value={simVal}
                onChange={(e) => setSimVal(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-14 h-9 border-y border-gray-200 text-center text-sm font-semibold text-[#1F2937] focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                data-testid="sim-input"
              />
              <button
                onClick={() => setSimVal(simVal + 1)}
                className="w-9 h-9 border border-gray-200 bg-[#F8FAFC] rounded-r-md flex items-center justify-center text-[#0D9488] hover:bg-[#0D9488] hover:text-white hover:border-[#0D9488] transition-all"
                data-testid="sim-plus"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <span className="text-sm text-[#0D9488] font-medium bg-[#0D9488]/10 px-3 py-1 rounded-full" data-testid="sim-palier">
              {simData.label}
            </span>
          </div>
          <div className="p-7">
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { name: 'Pack Essentiel', bg: 'bg-[#0F766E]', abo: '—', dpae: '500 F/decl.', data: simData.essentiel },
                { name: 'Pack Confort', bg: 'bg-[#0D9488]', abo: '3 500 F', dpae: '500 F/decl.', data: simData.confort },
                { name: 'Pack Serenite', bg: 'bg-[#14B8A6]', abo: '6 500 F', dpae: 'Incluses', data: simData.serenite },
              ].map(card => (
                <div key={card.name} className="border border-gray-200 rounded-lg overflow-hidden" data-testid={`sim-card-${card.name.split(' ')[1].toLowerCase()}`}>
                  <div className={`${card.bg} px-5 py-4`}>
                    <p className="text-base font-bold text-white">{card.name}</p>
                  </div>
                  <div className="px-5 py-4 space-y-0">
                    <div className="flex justify-between text-sm py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Abonnement outils</span>
                      <span className="font-semibold text-[#1F2937]">{card.abo}</span>
                    </div>
                    <div className="flex justify-between text-sm py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">Bulletins MO+CST</span>
                      <span className="font-semibold text-[#1F2937]">{fmt(simData.bul)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-1.5 border-b border-gray-100">
                      <span className="text-gray-500">DPAE</span>
                      <span className="font-semibold text-[#1F2937]">{card.dpae}</span>
                    </div>
                    <div className="flex justify-between text-sm py-2.5 mt-1 border-t border-gray-200">
                      <span className="font-semibold text-[#1F2937]">Mensuel HT</span>
                      <span className="text-lg font-bold text-[#F59E0B]">{fmt(card.data.m)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-1">
                      <span className="text-gray-500">Annuel HT</span>
                      <span className="text-gray-400">{fmt(card.data.a)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-5 text-xs text-gray-400 italic leading-relaxed">
              Simulation indicative hors DPAE. Les bulletins sont factures à l'acte selon votre effectif réel chaque mois. L'abonnement outils est fixe mensuel.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
